import { Banner } from "@brexhq/component-library";
import Cookies from "js-cookie";
import { isEmpty as lodashIsEmpty, values as lodashValues } from "lodash";
import React, { useCallback, useEffect, useState } from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { LeadFormValues } from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import {
  getAttributionMetadata,
  getFBC,
  getFBP,
  getGCLID,
  getMSCLKID,
  getLiFatId,
} from "@brexhq/next/src/telemetry/attributionMetaData";
import type { UserProperties } from "@brexhq/next/src/utils/signup";
import updateCookie from "@brexhq/next/src/utils/updateCookies";
import { LeadSelfAttestedType } from "@brexhq/next/types/brex-api";

import { getApiEndpoint } from "../../utils/endpoints";

import { ActionsBox, PrimaryButtonContainer, SubmitError } from "./elements";

export const genericErrorBannerTitle = "Something went wrong";
export const genericErrorBanner = (
  <Banner variant="error" title={genericErrorBannerTitle}>
    {"Please refresh your page to try again. If the problem persists, contact "}
    <a href="mailto:concierge@brex.com">concierge@brex.com</a>.
  </Banner>
);

export const unableToCreateAccountTitle = "Unable to create account";
export const unableToCreateAccountBanner = (email: string) => (
  <Banner variant="error" title={unableToCreateAccountTitle}>
    <div className="_lr-hide dd-privacy-mask">
      We were unable to create your account with email {email}. Please contact{" "}
      <a href="mailto:concierge@brex.com">concierge@brex.com</a>.
    </div>
  </Banner>
);

export const formFieldNames = {
  firstName: "firstName",
  lastName: "lastName",
  workEmail: "workEmail",
  password: "password",
  companyName: "companyName",
  attributionSource: "attributionSource",
  selfAttestedRefCode: "selfAttestedRefCode",
  howDidYouHear: "howDidYouHear",
  platformAgreement: "platformAgreement",
} as const;

export type FormFieldNames = typeof formFieldNames;
export type FormFieldValuesType = {
  [fieldName in keyof Omit<FormFieldNames, "platformAgreement">]: string;
} & { platformAgreement: boolean };

// Custom replacement for ramda map that functions the same without the extra dependency
const rmap = <T extends Record<string, any>>(
  fn: (value: T[keyof T]) => any,
  obj: T,
): FormFieldValuesType => {
  const result = {} as any;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = fn(obj[key]);
    }
  }
  return result as FormFieldValuesType;
};

export const initialFormValues = rmap((name) => {
  if (name === "platformAgreement") return false;
  return "";
}, formFieldNames);

export type FormValues = typeof initialFormValues;

export type SetFormValues = (
  setFormValues: (formValues: FormValues) => FormValues,
) => void;

export type FormStates = {
  [key: string]: {
    showError: boolean;
  };
};

export type SetFormStates = (
  setFormStates: (formStates: FormStates) => FormStates,
) => void;

export const Actions: React.FC<{
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  error?: React.ReactNode;
}> = ({ primary, secondary, error }) => (
  <ActionsBox>
    {error && <SubmitError>{error}</SubmitError>}
    <Container display="flex" justifyContent="flex-end" alignItems="center">
      {secondary}
      <PrimaryButtonContainer>{primary}</PrimaryButtonContainer>
    </Container>
  </ActionsBox>
);

type SubmitArgs = {
  event: React.FormEvent;
  formProps: {
    [prop: string]: {
      error: React.ReactElement | string | null;
    };
  };
};

export const useSubmit = () => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<React.ReactNode>();

  const submit = useCallback(
    ({
      event,
      formProps,
    }: SubmitArgs): {
      hasErrors: boolean;
    } => {
      event.preventDefault();
      setSubmitAttempted(true);
      const hasErrors = Object.values(formProps).some(({ error }) => !!error);
      if (hasErrors) {
        setSubmitError(
          <Banner variant="error" title="Missing information">
            Complete the required fields and correct any errors to continue.
          </Banner>,
        );
        return {
          hasErrors,
        };
      }

      setIsSubmitting(true);
      return { hasErrors };
    },
    [],
  );

  return {
    submit,
    submitAttempted,
    submitError,
    setSubmitError,
    isSubmitting,
    setIsSubmitting,
  };
};

/**
 * We use this function to retrieve the userProperty values.
 * Most of them are based off of formValues, but good to keep the consistency
 * in case we want to use these userProperties anywhere else!
 * Benefit here is that any changes or updates done here will be applied
 * to any where else we'd use user properties.
 */
export const getUserProperties = (formValues: FormValues): UserProperties => {
  const firstName = formValues.firstName;
  const lastName = formValues.lastName;
  const email = formValues.workEmail;
  const password = formValues.password;
  const companyName = formValues.companyName;
  const attributionSource = formValues.attributionSource;
  const selfAttestedRefCode = formValues.selfAttestedRefCode;
  const platformAgreement = formValues.platformAgreement;
  const howDidYouHear = formValues.howDidYouHear;
  const attributionToken =
    Cookies.get("ref_code") ||
    // We treat rc as if it's a ref_code param
    // This is used for referral links in the format
    // brex.com/signup?rc=whatever
    // Related: UrlParameters will clear the ref_code when setting the rc Cookie, so that it can be picked up here
    Cookies.get("rc") ||
    "";
  const fbp = getFBP();
  const fbc = getFBC();
  const gclid = getGCLID();
  const msclkid = getMSCLKID();
  const liFatId = getLiFatId();
  const attributionMetadata = getAttributionMetadata(
    fbp,
    fbc,
    gclid,
    msclkid,
    liFatId,
  );
  const leadToken = Cookies.get("leadToken") || null;

  return {
    firstName,
    lastName,
    email,
    password,
    companyName,
    attributionSource,
    attributionToken,
    selfAttestedRefCode,
    attributionMetadata,
    platformAgreement,
    howDidYouHear,
    leadToken,
  };
};

/**
 * For M7 user properties
 */
export const getLeadProperties = (formValues: LeadFormValues) => {
  const firstName = formValues.firstName;
  const lastName = formValues.lastName;
  const email = formValues.workEmail;
  const password = formValues.password;
  const companyName = formValues.companyName;
  const selfAttestedRefCode = formValues.refCode;
  const attributionToken =
    Cookies.get("ref_code") ||
    // We treat rc as if it's a ref_code param
    // This is used for referral links in the format
    // brex.com/signup?rc=whatever
    // Related: UrlParameters will clear the ref_code when setting the rc Cookie, so that it can be picked up here
    Cookies.get("rc") ||
    "";
  const fbp = getFBP();
  const fbc = getFBC();
  const gclid = getGCLID();
  const msclkid = getMSCLKID();
  const liFatId = getLiFatId();
  const attributionMetadata = getAttributionMetadata(
    fbp,
    fbc,
    gclid,
    msclkid,
    liFatId,
  );
  const leadToken = Cookies.get("leadToken") || null;
  const companyType = formValues.isEcomm || LeadSelfAttestedType.OTHER;
  const employeeCount = formValues.employeeCount;
  const fundingSources = formValues.fundingSources;
  const salesVolume = formValues.monthlySales;
  const industry = formValues.industry;
  const website = formValues.website;
  const salesSegment = formValues.salesSegment;

  return {
    firstName,
    lastName,
    email,
    password,
    companyName,
    attributionToken,
    selfAttestedRefCode,
    attributionMetadata,
    leadToken,
    companyType,
    employeeCount,
    fundingSources,
    salesVolume,
    industry,
    website,
    salesSegment,
  };
};

export const requiredFieldMessage = "This is a required field";

export interface PartnerRefValues {
  disclaimer?: string;
  token: string;
  customization?: {
    title?: string;
    subheader?: string;
    cta?: string;
    isIncorporated?: boolean;
  };
}

export interface UserPrefillData {
  firstName: string | null;
  lastName: string | null;
  workEmail: string | null;
  companyName: string | null;
  companyType: string | null;
  employeeCount: string | null;
  companyUrl: string | null;
  disclaimer: string | null;
  attributionToken: string | null;
  partnerName: string | null;
  partnerLogoUrl: string | null;
}

export const usePartnerPrefill = () => {
  // NOTE: Leaving here in case we want to show users an error state in the future
  // const [prefillError, setPrefillError] = useState<React.ReactNode>(null);
  const [partnerRef, setPartnerRef] = useState<PartnerRefValues | null>(null);

  const [userPrefillData, setUserPrefillData] = useState<
    UserPrefillData | undefined
  >(undefined);

  const setPrefillUserInfo = useCallback(
    async (token: string) => {
      const apiEndpoint: string = getApiEndpoint();
      // TODO: update attributionToken to be self_attested_ref_code
      const prefillDataQuery = `
      query ssuPrefillDataQuery($input: String!) {
        ssuPrefillData(referralIdentifier: $input) {
          firstName
          lastName
          workEmail
          companyName
          companyType
          employeeCount
          companyUrl
          disclaimer
          attributionToken
          partnerName
          partnerLogoUrl
          customization {
            title
            subheader
            cta
            isIncorporated
          }
        }
      }
  `;
      try {
        const prefillResponse = await fetch(apiEndpoint, {
          method: "POST",
          body: JSON.stringify({
            query: prefillDataQuery,
            variables: {
              input: token,
            },
          }),
          headers: new Headers({ "Content-Type": "application/json" }),
        });

        //Error on network request.
        if (!prefillResponse.ok) {
          console.error(
            `Network request failed to fetch user data for token: ${token}`,
          );

          return;
        }
        const userPrefillData = await prefillResponse.json();
        //Error on query.
        if (userPrefillData.errors) {
          console.error(
            `Failed to query partner ref user data for token: ${token}`,
            userPrefillData.errors,
          );

          return;
        }

        const ssuPrefillData = userPrefillData.data.ssuPrefillData;
        const isEmpty = lodashValues(ssuPrefillData).every(lodashIsEmpty);
        if (!isEmpty) {
          updateCookie("ref_code", ssuPrefillData.attributionToken);
          setUserPrefillData(ssuPrefillData);
          setPartnerRef({
            token,
            disclaimer: ssuPrefillData.disclaimer,
            customization: ssuPrefillData.customization,
          });
        }
      } catch (error) {
        console.error("Error in prefill:", error);
      }
    },
    [setUserPrefillData, setPartnerRef],
  );

  return {
    // NOTE: Leaving here in case we want to show users an error state in the future
    // prefillError,
    partnerRef,
    userPrefillData,
    setPrefillUserInfo,
  };
};

export const usePartnerPrefillFromSearchParams = (search?: string) => {
  const { userPrefillData, partnerRef, setPrefillUserInfo } =
    usePartnerPrefill();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const cookiePrefToken = Cookies.get("pref_token");
    // query param takes precedence, otherwise check if cookie exists
    if (params && params?.has("pref_token")) {
      const queryPrefToken = params?.get("pref_token") as string;
      setPrefillUserInfo(queryPrefToken);
      if (cookiePrefToken) {
        // update existing cookie value with query value
        updateCookie("pref_token", queryPrefToken);
      }
    } else if (cookiePrefToken) {
      setPrefillUserInfo(cookiePrefToken);
    }
  }, [search, setPrefillUserInfo]);

  return { userPrefillData, partnerRef };
};

export const shouldPrefillCustomerReferral = async (refCode: string) => {
  if (!refCode) {
    return false;
  }
  const apiEndpoint: string = getApiEndpoint();

  const isCustomerReferralQuery = `
    query isCustomerReferralQuery($input: String!) {
      customerReferral(referralCode: $input) {
        isCustomerReferral
      }
    }
`;
  try {
    const customerReferralResponse = await fetch(apiEndpoint, {
      method: "POST",
      body: JSON.stringify({
        query: isCustomerReferralQuery,
        variables: {
          input: refCode,
        },
      }),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    // Error on network request.
    if (!customerReferralResponse.ok) {
      console.error(
        `Network request failed to fetch referral data for code: ${refCode}`,
      );

      return;
    }
    const customerReferralData = await customerReferralResponse.json();
    // Error on query.
    if (customerReferralData.errors) {
      console.error(
        `Failed to query customer referral data for code: ${refCode}`,
        customerReferralData.errors,
      );

      return;
    }

    const customerReferral = customerReferralData.data.customerReferral;

    const isCustomerReferral = customerReferral.isCustomerReferral;
    return isCustomerReferral;
  } catch (error) {
    console.error("Error fetching customer referral:", error);
  }
};

export interface SignupUserLabels {
  firstName: string;
  lastName: string;
  workEmail: string;
  password: string;
  passwordPartnerRef: string;
  attributionToken: string;
  userSubmitButton: string;
  howDidYouHear: string;
}
export interface SignupFormLabels {
  user: SignupUserLabels;
  company: { companyName: string };
}

export const formLabels: SignupFormLabels = {
  user: {
    firstName: "Legal first name",
    lastName: "Legal last name",
    workEmail: "Work email",
    password: "Password",
    passwordPartnerRef: "Create password",
    attributionToken: "Referral code (optional)",
    howDidYouHear: "How did you hear about us? (optional)",
    userSubmitButton: "Start application",
  },
  company: {
    companyName: "Company name",
  },
};

export const getDashboardRedirectUrl = (
  session: Record<string, unknown> | undefined,
  usePreviewEnvironment: boolean,
  previewEnvironmentNamespace: string | undefined,
): URL => {
  // TODO: MJ Refactor to make sure we have this environment variable, check for when it's undefined
  const dashboardLoginUrl: string = usePreviewEnvironment
    ? `https://dashboard-pre-${previewEnvironmentNamespace}.brexhq.dev/login-with-session`
    : (process.env.DASHBOARD_LOGIN_URL as string);
  // : "http://localhost:3000/login-with-session";
  // Swap to test signup with local dashboard instance
  const dashboardRedirectUrl = new URL(dashboardLoginUrl);

  if (session) {
    dashboardRedirectUrl.searchParams.append(
      "session",
      JSON.stringify(session),
    );
  }

  return dashboardRedirectUrl;
};
